/**
 * Statusmaschine eines Basars (Quelle der Wahrheit für API, Web und App), siehe docs/01-ARCHITEKTUR.md Abschnitt 9.
 * draft -> published -> registration_open -> intake -> selling -> returns -> closed -> settled -> archived
 */
export const EVENT_STATUSES = [
  'draft',
  'published',
  'registration_open',
  'intake',
  'selling',
  'returns',
  'closed',
  'settled',
  'archived',
] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export function isEventStatus(value: string): value is EventStatus {
  return (EVENT_STATUSES as readonly string[]).includes(value);
}

/**
 * Erlaubte Übergänge. Vorwärts immer nur ein Schritt. Zurück nur, solange noch nichts Unumkehrbares geschah:
 * Verkauf und Rückgabe dürfen hin- und hergehen (Nachzügler-Verkäufe), ab „closed“ geht es nur noch vorwärts.
 */
const TRANSITIONS: Readonly<Record<EventStatus, readonly EventStatus[]>> = {
  draft: ['published'],
  published: ['registration_open', 'draft'],
  registration_open: ['intake', 'published'],
  intake: ['selling', 'registration_open'],
  selling: ['returns', 'intake'],
  returns: ['closed', 'selling'],
  closed: ['settled'],
  settled: ['archived'],
  archived: [],
};

export function allowedTransitions(from: EventStatus): readonly EventStatus[] {
  return TRANSITIONS[from];
}

export function canTransition(from: EventStatus, to: EventStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

/**
 * Nur Veröffentlichen und Öffnen der Anmeldung setzen eine aktive Lizenz voraus (Abschnitt 9). Alle anderen Wechsel
 * (Annahme, Verkauf, Rückgabe, Abschluss) sind nie an die Lizenz gebunden: Am Basar-Tag ist sie kein Blocker.
 */
export function requiresActiveLicense(to: EventStatus): boolean {
  return to === 'published' || to === 'registration_open';
}

/** Diese Funktionen sind je Status freigeschaltet (Tabelle in Abschnitt 9). */
export interface EventCapabilities {
  sellersMayEditItems: boolean;
  intakeOpen: boolean;
  cashOpen: boolean;
  returnsOpen: boolean;
  /** Basar-Sitzungen (Helfer, Kasse) bestehen weiter. */
  teamSessionsActive: boolean;
}

export function capabilitiesOf(status: EventStatus): EventCapabilities {
  return {
    sellersMayEditItems:
      status === 'draft' ||
      status === 'published' ||
      status === 'registration_open' ||
      status === 'intake',
    intakeOpen: status === 'intake',
    cashOpen: status === 'selling',
    returnsOpen: status === 'returns',
    teamSessionsActive: status === 'intake' || status === 'selling' || status === 'returns',
  };
}

/**
 * Freigabe einer Verkäufernummer (Absage, Inaktivität) nur vor der Warenannahme: Entwurf, veröffentlicht und offene
 * Anmeldung. Ab „intake“ stehen Nummern und Etiketten fest, sie bleiben dann gebunden.
 */
export function sellerNumberReleaseOpen(status: string): boolean {
  return status === 'draft' || status === 'published' || status === 'registration_open';
}
